import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { GOOGLE_CLIENT_ID, SCOPES } from '@/config/google'
import { loginWithGoogle, setApiContext } from '@/services/apiService'

const SESSION_KEY = (slug) => `rb_session:${slug}`

export const useAuthStore = defineStore('auth', () => {
  const user            = ref(null)   // Google profile { name, email, picture }
  const currentUser     = ref(null)   // Sheet user row  { UserID, UserName, Email, Role, … }
  const sessionJwt      = ref(null)   // Signed JWT issued by the CF backend
  const jwtClaims       = ref(null)   // Decoded JWT payload (email, slug, role)
  const loading         = ref(false)
  const error           = ref(null)
  const sessionRestored = ref(false)

  const isAuthenticated = computed(() => !!sessionJwt.value)
  const isAdmin         = computed(() => jwtClaims.value?.role === 'Admin')

  // ── Code Client (authorization code flow) ────────────────────────────────
  // The browser receives only a short-lived code; Google tokens never leave
  // the server. The backend exchanges the code using GOOGLE_CLIENT_SECRET.

  let codeClient = null

  function initCodeClient () {
    return new Promise((resolve) => {
      if (codeClient) return resolve(codeClient)
      const check = setInterval(() => {
        if (window.google?.accounts?.oauth2) {
          clearInterval(check)
          codeClient = window.google.accounts.oauth2.initCodeClient({
            client_id: GOOGLE_CLIENT_ID,
            scope:     SCOPES,
            ux_mode:   'popup',
            callback:  () => {}
          })
          resolve(codeClient)
        }
      }, 100)
    })
  }

  async function requestAuthCode () {
    const client = await initCodeClient()
    return new Promise((resolve, reject) => {
      client.callback = (res) => {
        if (res.error) return reject(new Error(res.error))
        resolve(res.code)
      }
      client.requestCode()
    })
  }

  // ── Sign In ───────────────────────────────────────────────────────────────

  async function signIn (slug) {
    loading.value = true
    error.value   = null
    try {
      const code   = await requestAuthCode()
      const result = await loginWithGoogle({ code, slug, redirectUri: window.location.origin })

      user.value        = result.user
      currentUser.value = result.currentUser
      sessionJwt.value  = result.sessionJwt
      jwtClaims.value   = decodeJwt(result.sessionJwt)
      setApiContext(slug, result.sessionJwt)

      saveSession(slug)
    } catch (e) {
      error.value = e.message || 'Sign-in failed'
      clearSession(slug)
    } finally {
      loading.value = false
    }
  }

  // ── Resolve current user (retry helper for BookingModal) ─────────────────

  async function resolveCurrentUser () {
    const { getUsers } = await import('@/services/apiService')
    const users = await getUsers()
    const match = users.find(u => u.Email?.toLowerCase() === user.value?.email?.toLowerCase())
    if (match) currentUser.value = match
    return match || null
  }

  // ── Session persistence ────────────────────────────────────────────────────

  function jwtExpiry (token) {
    try {
      const body = token.split('.')[1]
      const payload = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')))
      return payload.exp ? payload.exp * 1000 : 0
    } catch { return 0 }
  }

  function decodeJwt (token) {
    try {
      const body = token.split('.')[1]
      return JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')))
    } catch { return null }
  }

  function saveSession (slug) {
    localStorage.setItem(SESSION_KEY(slug), JSON.stringify({
      user:        user.value,
      currentUser: currentUser.value,
      sessionJwt:  sessionJwt.value,
      jwtExpiry:   jwtExpiry(sessionJwt.value)
    }))
  }

  function clearSession (slug) {
    if (slug) localStorage.removeItem(SESSION_KEY(slug))
    user.value        = null
    currentUser.value = null
    sessionJwt.value  = null
    jwtClaims.value   = null
  }

  async function restoreSession (slug) {
    if (sessionRestored.value) return
    const saved = localStorage.getItem(SESSION_KEY(slug))
    if (!saved) { sessionRestored.value = true; return }

    let parsed
    try { parsed = JSON.parse(saved) } catch { sessionRestored.value = true; return }

    // Reject if JWT is missing or expired
    if (!parsed.sessionJwt || (parsed.jwtExpiry && Date.now() > parsed.jwtExpiry)) {
      clearSession(slug)
      sessionRestored.value = true
      return
    }

    user.value        = parsed.user
    currentUser.value = parsed.currentUser
    sessionJwt.value  = parsed.sessionJwt
    jwtClaims.value   = decodeJwt(parsed.sessionJwt)
    setApiContext(slug, parsed.sessionJwt)
    sessionRestored.value = true
  }

  function signOut (slug) {
    clearSession(slug)
  }

  return {
    user, currentUser, sessionRestored, sessionJwt, jwtClaims, loading, error,
    isAuthenticated, isAdmin,
    signIn, signOut, restoreSession, resolveCurrentUser
  }
})


